import React from 'react';
import { BarChart2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid, LabelList } from 'recharts';
import { Player, Language } from '../../types';
import { t } from '../../i18n';

interface PlayerStatsChartProps {
    player: Player;
    lang: Language;
    metric: 'goals' | 'assists' | 'appearances' | 'rating';
    onChangeMetric: (m: 'goals' | 'assists' | 'appearances' | 'rating') => void;
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 px-4 py-3 rounded-xl border border-white/10 shadow-2xl">
          <p className="font-black text-slate-400 text-[9px] uppercase tracking-[0.2em] mb-2">{label} Season</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-3 justify-between min-w-[80px]">
              <span className="text-white text-[10px] font-black uppercase tracking-tight">{entry.name}</span>
              <span className="font-black text-rose-500 text-sm tabular-nums">
                {entry.name === 'Rating' ? Number(entry.value).toFixed(2) : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
};

export const PlayerStatsChart: React.FC<PlayerStatsChartProps> = ({ player, lang, metric, onChangeMetric }) => {
    const metrics = [
        { id: 'goals', label: t(lang, 'goals') },
        { id: 'assists', label: t(lang, 'assists') },
        { id: 'appearances', label: t(lang, 'matches') },
        { id: 'rating', label: t(lang, 'rating') },
    ] as const;

    // Create 5-year data array, fill gaps with 0
    const data = player.stats
        .slice(0, 5)
        .reverse()
        .map(s => ({
            name: `${s.year}`,
            Value: metric === 'rating' ? parseFloat(Number(s[metric] || 0).toFixed(2)) : (s[metric] || 0)
        }));

    return (
        <section>
             <div className="mb-8">
                {/* Title Section */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-slate-900 rounded-full"></div>
                    <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase">{t(lang, 'performanceHistory')}</h2>
                </div>
                
                {/* Metric Menu - Positioned Below Title */}
                <div className="bg-gray-50 p-1 rounded-xl flex items-center gap-1 overflow-x-auto no-scrollbar border border-gray-100 w-fit max-w-full">
                    {metrics.map(m => (
                        <button 
                            key={m.id} 
                            onClick={() => onChangeMetric(m.id)} 
                            className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                                metric === m.id 
                                ? 'bg-white text-slate-900 shadow-sm border border-gray-100' 
                                : 'text-slate-400 md:hover:text-slate-600'
                            }`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
             </div>
             
             <div className="w-full aspect-video md:aspect-[21/9] min-h-[256px] relative">
                 {data.length > 0 ? (
                     <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={100}>
                        <BarChart data={data} margin={{left: -20, right: 10, top: 20, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 800}} 
                                dy={10} 
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{fill: '#f8fafc', radius: 4}} />
                            <Bar 
                                dataKey="Value" 
                                fill={metric === 'rating' ? "#f59e0b" : "#f43f5e"} 
                                radius={[4, 4, 0, 0]} 
                                barSize={28}
                                animationDuration={1500}
                            >
                                <LabelList 
                                    dataKey="Value" 
                                    position="top" 
                                    style={{ fontSize: '10px', fontWeight: '900', fill: '#64748b' }}
                                    formatter={(val: number) => metric === 'rating' ? val.toFixed(2) : val.toLocaleString()}
                                />
                            </Bar>
                        </BarChart>
                     </ResponsiveContainer>
                 ) : (
                     <div className="h-full flex flex-col items-center justify-center text-slate-200">
                         <BarChart2 size={40} className="mb-2 opacity-10" />
                         <p className="text-[10px] font-black uppercase tracking-widest">{t(lang, 'noStatsSynced')}</p>
                     </div>
                 )}
             </div>
        </section>
    );
};
